import { HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, distinctUntilChanged, map, of, startWith, switchMap } from 'rxjs';
import { ProductionEfficiencyDetailDialogComponent } from 'src/app/components/main/employees/production/production-efficiency-matrix/production-efficiency-detail-dialog/production-efficiency-detail-dialog.component';
import {
  ProductionEfficiencyCellColor,
  ProductionEfficiencyMatrixCell,
  ProductionEfficiencyMatrixResponse,
} from 'src/app/models/production-efficiency-matrix.model';
import { ApiService } from 'src/app/services/api.service';

const DEFAULT_PROFILE_PICTURE = 'assets/img/default_profile_picture.png';
const DEFAULT_COMPANY_PRODUCT_LOGO = '/img/default-company_product.png';

const COLOR_CSS: Record<ProductionEfficiencyCellColor, string> = {
  green: 'var(--green)',
  orange: 'var(--yellow)',
  red: 'var(--red)',
};

@Component({
  selector: 'app-production-efficiency-matrix',
  templateUrl: './production-efficiency-matrix.component.html',
  styleUrl: './production-efficiency-matrix.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinnerModule, MatTooltipModule],
})
export class ProductionEfficiencyMatrixComponent {
  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);

  readonly from_month = input.required<string>();
  readonly to_month = input.required<string>();

  private readonly matrix_request = toSignal(
    toObservable(
      computed((): { from_month: string; to_month: string } => ({
        from_month: this.from_month(),
        to_month: this.to_month(),
      }))
    ).pipe(
      distinctUntilChanged(
        (previous, current): boolean => previous.from_month === current.from_month && previous.to_month === current.to_month
      ),
      switchMap(({ from_month, to_month }) =>
        this.api.getProductionEfficiencyMatrix(new HttpParams().set('from_month', from_month).set('to_month', to_month)).pipe(
          map(matrix => ({ status: 'ready' as const, matrix, error: null as string | null })),
          catchError(() =>
            of({
              status: 'error' as const,
              matrix: null as ProductionEfficiencyMatrixResponse | null,
              error: 'No se ha podido cargar la matriz de eficiencia productiva.',
            })
          ),
          startWith({
            status: 'loading' as const,
            matrix: null as ProductionEfficiencyMatrixResponse | null,
            error: null as string | null,
          })
        )
      )
    ),
    {
      initialValue: {
        status: 'loading' as const,
        matrix: null as ProductionEfficiencyMatrixResponse | null,
        error: null as string | null,
      },
    }
  );

  readonly is_loading = computed((): boolean => this.matrix_request().status === 'loading');
  readonly error_message = computed((): string | null => this.matrix_request().error);

  readonly view_model = computed((): ProductionEfficiencyMatrixViewModel | null => {
    const matrix = this.matrix_request().matrix;
    if (matrix === null) {
      return null;
    }

    const columns = matrix.columns.map(
      (column): ProductionEfficiencyColumnView => ({
        id: column.id,
        name: column.name,
        logo_url: column.logo_url || DEFAULT_COMPANY_PRODUCT_LOGO,
        general: this.serializeCell(matrix.column_generals[String(column.id)] ?? null),
      })
    );

    const rows = matrix.rows.map((row): ProductionEfficiencyRowView => {
      const cells = columns.map((column): ProductionEfficiencyCellView => {
        const serialized = this.serializeCell(matrix.cells[`${row.id}|${column.id}`] ?? null);
        return {
          user_id: row.id,
          company_product_id: column.id,
          real_label: serialized?.real_label ?? null,
          expected_label: serialized?.expected_label ?? null,
          color_css: serialized?.color_css ?? null,
          tooltip: serialized?.tooltip ?? 'Ver detalle del cálculo',
          clickable: true,
        };
      });

      return {
        id: row.id,
        name: row.name,
        informal_photo_url: row.profile_picture_url || DEFAULT_PROFILE_PICTURE,
        formal_photo_url: row.serious_profile_image_url || row.profile_picture_url || DEFAULT_PROFILE_PICTURE,
        general: this.serializeCell(matrix.row_generals[String(row.id)] ?? null),
        cells,
      };
    });

    return {
      columns,
      rows,
      global_general: this.serializeCell(matrix.global_general),
    };
  });

  openCellDetail(cell: ProductionEfficiencyCellView): void {
    this.dialog.open(ProductionEfficiencyDetailDialogComponent, {
      data: {
        from_month: this.from_month(),
        to_month: this.to_month(),
        user_id: cell.user_id,
        company_product_id: cell.company_product_id,
      },
      width: '960px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  private serializeCell(
    cell: ProductionEfficiencyMatrixCell | null
  ): { real_label: string; expected_label: string; color_css: string; tooltip: string } | null {
    if (cell === null) {
      return null;
    }

    return {
      real_label: this.formatPercent(cell.real_percent),
      expected_label: this.formatPercent(cell.expected_percent),
      color_css: COLOR_CSS[cell.color],
      tooltip: cell.tooltip,
    };
  }

  private formatPercent(value: number): string {
    return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}%`;
  }
}

interface ProductionEfficiencyCellView {
  user_id: number;
  company_product_id: number;
  real_label: string | null;
  expected_label: string | null;
  color_css: string | null;
  tooltip: string;
  clickable: boolean;
}

interface ProductionEfficiencyColumnView {
  id: number;
  name: string;
  logo_url: string;
  general: { real_label: string; expected_label: string; color_css: string; tooltip: string } | null;
}

interface ProductionEfficiencyRowView {
  id: number;
  name: string;
  informal_photo_url: string;
  formal_photo_url: string;
  general: { real_label: string; expected_label: string; color_css: string; tooltip: string } | null;
  cells: ProductionEfficiencyCellView[];
}

interface ProductionEfficiencyMatrixViewModel {
  columns: ProductionEfficiencyColumnView[];
  rows: ProductionEfficiencyRowView[];
  global_general: { real_label: string; expected_label: string; color_css: string; tooltip: string } | null;
}
